import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar,
    Box,
    CssBaseline,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    Button,
    useTheme,
    alpha,
    Tooltip,
    Badge,
    SvgIcon,
    Chip
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    Message as MessageIcon,
    People as PeopleIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    WhatsApp as WhatsAppIcon,
    Campaign as CampaignIcon,
    SmartToy as AssistantIcon,
    ChevronRight as ChevronRightIcon,
    Send as SendIcon,
    QuestionAnswerTwoTone,
    AccountBalance as AccountBalanceIcon,
    SwapHoriz as SwitchIcon,
    KeyboardReturn as ReturnIcon,
    Interests as IntegrationIcon,
    Assessment as AnalysisIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import UserSwitchModal from './UserSwitchModal';

const drawerWidth = 240;

// Custom color palette for the purple, lilac, and pink theme
const customColors = {
    purple: '#6a1b9a',
    purpleLight: '#9c4dcc',
    purpleDark: '#38006b',
    lilac: '#9575cd',
    lilacLight: '#c7a4ff',
    lilacDark: '#65499c',
    pink: '#ec407a',
    pinkLight: '#ff77a9',
    pinkDark: '#b4004e',
};

// Modern logo component
const ModernLogo = (props) => {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ...props.sx }}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <SvgIcon
                    sx={{
                        fontSize: '2.5rem',
                        color: customColors.lilacLight,
                        filter: 'drop-shadow(0 0 2px rgba(236, 64, 122, 0.5))',
                        transform: 'rotate(0deg)',
                        mr: 1,
                        zIndex: 2
                    }}
                >
                    <QuestionAnswerTwoTone />
                </SvgIcon>
                <Typography
                    variant="h4"
                    component="span"
                    sx={{
                        fontWeight: 800,
                        letterSpacing: '0.5px',
                        background: `linear-gradient(45deg, ${customColors.purple} 15%, ${customColors.lilac} 50%, ${customColors.pink} 85%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '0 2px 10px rgba(156, 39, 176, 0.3)',
                        ml: 0.5
                    }}
                >
                    WA
                </Typography>
                <Typography
                    variant="h5"
                    component="span"
                    sx={{
                        fontWeight: 300,
                        letterSpacing: '1px',
                        background: `linear-gradient(45deg, ${customColors.lilacLight} 30%, ${customColors.pinkLight} 90%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        ml: 1
                    }}
                >
                    Offers
                </Typography>
            </Box>
        </Box>
    );
};

const DashboardLayout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const { user, originalUser, logout, isSwitchedUser, returnToOriginalUser } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [activePath, setActivePath] = useState('');
    const [switchModalOpen, setSwitchModalOpen] = useState(false);

    useEffect(() => {
        setActivePath(location.pathname);
    }, [location]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleSwitchUser = () => {
        setSwitchModalOpen(true);
        handleProfileMenuClose();
    };

    const handleReturnToOriginal = () => {
        returnToOriginalUser();
        handleProfileMenuClose();
    };

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'Assistants', icon: <AssistantIcon />, path: '/assistants' },
        { text: 'Integrations', icon: <IntegrationIcon />, path: '/integrations' },
        { text: 'WA Accounts', icon: <WhatsAppIcon />, path: '/accounts' },
        { text: 'Chats', icon: <MessageIcon />, path: '/messages' },
        { text: 'Contacts', icon: <PeopleIcon />, path: '/contacts' },
        { text: 'Campaigns', icon: <CampaignIcon />, path: '/campaigns' },
        { text: 'Analysis', icon: <AnalysisIcon />, path: '/campaign-analysis' },
        { text: 'Finance', icon: <AccountBalanceIcon />, path: '/finance' },
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings' }
    ];

    const drawer = (
        <Box
            sx={{
                height: '100%',
                background: `linear-gradient(135deg, ${customColors.purpleDark} 0%, ${customColors.purple} 40%, ${customColors.lilac} 80%, ${customColors.lilacLight} 100%)`,
                color: 'white',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Toolbar
                sx={{
                    justifyContent: 'center',
                    py: 2,
                    background: alpha(customColors.purpleDark, 0.3),
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.15)'
                }}
            >
                <ModernLogo />
            </Toolbar>
            <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, py: 3 }}>
                <List sx={{ '& .MuiListItem-root': { mb: 1 } }}>
                    {menuItems.map((item) => {
                        const isActive = activePath === item.path;
                        return (
                            <ListItem key={item.text} disablePadding>
                                <ListItemButton
                                    onClick={() => navigate(item.path)}
                                    sx={{
                                        borderRadius: '12px',
                                        transition: 'all 0.3s ease',
                                        position: 'relative',
                                        pl: isActive ? 3 : 2,
                                        py: 1.5,
                                        backgroundColor: isActive
                                            ? alpha(customColors.pink, 0.2)
                                            : 'transparent',
                                        '&:hover': {
                                            backgroundColor: alpha(customColors.pink, 0.1),
                                            pl: 3,
                                            '& .MuiListItemIcon-root': {
                                                transform: 'scale(1.1)',
                                                color: customColors.pinkLight
                                            }
                                        },
                                        '&::before': isActive ? {
                                            content: '""',
                                            position: 'absolute',
                                            left: 0,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            height: '60%',
                                            width: '4px',
                                            background: `linear-gradient(to bottom, ${customColors.pink}, ${customColors.lilacLight})`,
                                            borderRadius: '0 4px 4px 0',
                                            boxShadow: `0 0 8px ${customColors.pink}`
                                        } : {}
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        color: 'white',
                                        minWidth: '40px',
                                        transition: 'transform 0.2s ease'
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{
                                            fontWeight: isActive ? 'bold' : 'normal',
                                            fontSize: '0.95rem',
                                            letterSpacing: '0.2px'
                                        }}
                                    />
                                    {isActive && <ChevronRightIcon sx={{ opacity: 0.7 }} />}
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>
            <Box
                sx={{
                    p: 2,
                    background: alpha(customColors.purpleDark, 0.5),
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: '1px' }}>
                    © {new Date().getFullYear()} WA Offers
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    ml: { sm: `${drawerWidth}px` },
                    background: 'white',
                    color: 'text.primary',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)'
                }}
            >
                <Toolbar sx={{ minHeight: '70px' }}>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{
                            flexGrow: 1,
                            fontWeight: 600,
                            background: `linear-gradient(45deg, ${customColors.purple} 30%, ${customColors.lilac} 90%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        {menuItems.find(item => item.path === activePath)?.text || 'Dashboard'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* User switched indicator */}
                        {isSwitchedUser() && (
                            <Chip
                                label={`Acting as: ${user?.name}`}
                                color="secondary"
                                size="small"
                                sx={{
                                    fontWeight: 500,
                                    backgroundColor: alpha(customColors.pink, 0.1),
                                    color: customColors.pink,
                                    border: `1px solid ${alpha(customColors.pink, 0.3)}`
                                }}
                            />
                        )}

                        <Tooltip title="Your profile">
                            <Button
                                onClick={handleProfileMenuOpen}
                                sx={{
                                    borderRadius: '28px',
                                    py: 1,
                                    px: 2,
                                    textTransform: 'none',
                                    backgroundColor: alpha(customColors.purple, 0.05),
                                    color: customColors.purple,
                                    '&:hover': {
                                        backgroundColor: alpha(customColors.purple, 0.1),
                                    },
                                    transition: 'all 0.2s ease'
                                }}
                                startIcon={
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            border: `2px solid ${customColors.lilac}`,
                                            backgroundColor: alpha(customColors.lilac, 0.1)
                                        }}
                                        alt={user?.name || 'User'}
                                    >
                                        {(user?.name || 'User').charAt(0).toUpperCase()}
                                    </Avatar>
                                }
                            >
                                {user?.name || 'User'}
                            </Button>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleProfileMenuClose}
                            PaperProps={{
                                elevation: 3,
                                sx: {
                                    borderRadius: '12px',
                                    minWidth: '200px',
                                    overflow: 'visible',
                                    mt: 1.5,
                                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <MenuItem
                                onClick={() => {
                                    navigate('/settings');
                                    handleProfileMenuClose();
                                }}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: '8px',
                                    mx: 1,
                                    my: 0.5,
                                    '&:hover': { backgroundColor: alpha(customColors.lilac, 0.05) }
                                }}
                            >
                                <ListItemIcon>
                                    <SettingsIcon fontSize="small" sx={{ color: customColors.purple }} />
                                </ListItemIcon>
                                <Typography variant="body2">Settings</Typography>
                            </MenuItem>

                            {/* Show switching option only for admins */}
                            {(originalUser?.role === 'admin' || (user?.role === 'admin' && !isSwitchedUser())) && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    {isSwitchedUser() ? (
                                        <MenuItem
                                            onClick={handleReturnToOriginal}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                borderRadius: '8px',
                                                mx: 1,
                                                my: 0.5,
                                                '&:hover': { backgroundColor: alpha(customColors.pink, 0.05) }
                                            }}
                                        >
                                            <ListItemIcon>
                                                <ReturnIcon fontSize="small" sx={{ color: customColors.pink }} />
                                            </ListItemIcon>
                                            <Typography variant="body2">Return to Admin</Typography>
                                        </MenuItem>
                                    ) : (
                                        <MenuItem
                                            onClick={handleSwitchUser}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                borderRadius: '8px',
                                                mx: 1,
                                                my: 0.5,
                                                '&:hover': { backgroundColor: alpha(customColors.purple, 0.05) }
                                            }}
                                        >
                                            <ListItemIcon>
                                                <SwitchIcon fontSize="small" sx={{ color: customColors.purple }} />
                                            </ListItemIcon>
                                            <Typography variant="body2">Switch User</Typography>
                                        </MenuItem>
                                    )}
                                </>
                            )}

                            <Divider sx={{ my: 1 }} />
                            <MenuItem
                                onClick={() => {
                                    handleLogout();
                                    handleProfileMenuClose();
                                }}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    borderRadius: '8px',
                                    mx: 1,
                                    my: 0.5,
                                    '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.05) }
                                }}
                            >
                                <ListItemIcon>
                                    <LogoutIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                <Typography variant="body2" color="error">Logout</Typography>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
                aria-label="mailbox folders"
            >
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    sx={{
                        display: { xs: 'block', sm: 'none' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                >
                    {drawer}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                    }}
                    open
                >
                    {drawer}
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
            >
                <Toolbar />
                {children}
            </Box>

            {/* User Switch Modal */}
            <UserSwitchModal
                open={switchModalOpen}
                onClose={() => setSwitchModalOpen(false)}
            />
        </Box>
    );
};

export default DashboardLayout;